/**
 *
 * LandingPagePreferences/ContactRegistrationFields
 *
 */

import React from 'react'

import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

// Components
import RadioButton from 'components/RadioButton'
import ReactTooltip from 'react-tooltip'
import Checkbox from 'components/Checkbox'
import { BlockedBanner } from 'components/Organizations/Index'

const Container = styled.div`
	.tooltip {
		max-width: 400px;
	}
`

const fields_template = [
	{ title: 'Email', name: 'email_field' },
	{ title: 'Phone', name: 'phone_field' },
	{ title: 'Address', name: 'address_field' },
	{ title: 'Notes', name: 'note_field', hide_required: true },
	{ title: 'Terms & Conditions (567 Campaigns)', name: 'terms_and_conditions_field' },
	{ title: 'Follow-Up Request (567 Campaigns)', name: 'follow_up_request_field', hide_required: true },
	{ title: 'Age Group', name: 'age_group_field', hide_for_organization: true },
]

const AGE_GROUPS = [
	{ value: 'under40', title: '< 40' },
	{ value: '50to54', title: '50-54' },
	{ value: '60to64', title: '60-64' },
	{ value: '70to74', title: '70-74' },
	{ value: '40to49', title: '40-49' },
	{ value: '55to59', title: '55-59' },
	{ value: '65to69', title: '65-69' },
	{ value: '75plus', title: '75+' },
]

const GroupFields = ({ title, name, value, handleFieldChange, disabled, hide_required = false, hide = false }) =>
	!hide ? (
		<div className="mb-1" role="group" aria-label="Email group">
			<div>
				<strong className="mr-2">{title}:</strong>
			</div>
			<div>
				{['Hide', 'Show', hide_required ? null : 'Required'].map((label, index) =>
					label ? (
						<RadioButton
							label={label}
							name={name}
							value={index}
							onChange={() => handleFieldChange({ target: { name, value: index } })}
							checked={value === index}
							disabled={disabled}
						/>
					) : null,
				)}
			</div>
		</div>
	) : null

const ContactRegistrationFields = ({
	state,
	handleInputChange,
	mergeState,
	confirm_digital_registrations, // this is a setting on the user, not landing_page_config, but it only applies to event campaigns.
	toggleConfirmDigitalRegistrations,
	is_for_organizations = false,
	accepted_age_groups = [],
	updateAcceptedAgeGroups,
	organization,
}) => {
	const disabled_by_organization = !!organization && !organization?.landing_page_config?.has_default_state

	const disabled_allow_appointments_by_organization =
		!!organization && organization?.landing_page_config?.disable_allow_appointments

	return (
		<div className="p-relative text-left my-3">
			{disabled_by_organization && (
				<BlockedBanner category="Landing Page Registration Fields" organization_name={organization.name} />
			)}

			<Container className="ml-4">
				<div className="d-flex flex-wrap">
					{is_for_organizations ? (
						<React.Fragment>
							<Checkbox
								label="Disable Appointment Registrations"
								checked={state.disable_allow_appointments || false}
								onChange={(bool) => mergeState({ disable_allow_appointments: bool })}
							/>

							<FontAwesomeIcon
								data-tip
								data-for="tooltip-1"
								icon="info-circle"
								size="md"
								className="text-primary ml-2 mt-1"
							/>
						</React.Fragment>
					) : (
						<React.Fragment>
							{!disabled_allow_appointments_by_organization && state.objective !== 1 && (
								<Checkbox
									label="Allow Appointment Registrations"
									checked={state.allow_appointments || false}
									onChange={(bool) => mergeState({ allow_appointments: bool })}
									disabled={disabled_by_organization}
								/>
							)}
						</React.Fragment>
					)}
				</div>

				{state.objective === 0 && (
					<div className="d-flex flex-wrap">
						<Checkbox
							label="Confirm Digital Registrations"
							checked={confirm_digital_registrations || false}
							onChange={toggleConfirmDigitalRegistrations}
							disabled={disabled_by_organization}
						/>

						<FontAwesomeIcon
							data-tip=""
							data-for="tooltip"
							icon="info-circle"
							size="md"
							className="text-primary ml-2 mt-1"
						/>

						<ReactTooltip id="tooltip" className="unzoom tooltip" place="right" effect="float">
							To reduce no-shows, contacts from digital lures must respond to an email or text to complete
							their registration for free events.
						</ReactTooltip>
					</div>
				)}

				{fields_template.map((field_props, index) => (
					<GroupFields
						key={index}
						value={state[field_props.name]}
						handleFieldChange={handleInputChange}
						disabled={disabled_by_organization}
						hide={is_for_organizations && field_props.hide_for_organization}
						{...field_props}
					/>
				))}

				{!is_for_organizations && (
					<div role="group" aria-label="Age Brackets">
						<div>
							<strong className="mr-2">Waitlist Digital Registrations not Selected:</strong>
							<FontAwesomeIcon
								data-tip
								data-for="tooltip-age-group"
								icon="info-circle"
								size="md"
								className="text-primary mt-1"
							/>

							<ReactTooltip
								id="tooltip-age-group"
								className="unzoom tooltip"
								place="right"
								effect="float"
							>
								<b>What's This?</b>
								<br />
								Any digital registrations for meal events that are not in an age group selected below
								will be added to the waitlist.
							</ReactTooltip>
						</div>

						<div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 85px)', padding: '5px 10px' }}>
							{AGE_GROUPS.map((age_group) => (
								<div key={age_group.value}>
									<Checkbox
										value={age_group.value}
										label={<span className="mr-2">{age_group.title}</span>}
										checked={accepted_age_groups.includes(age_group.value)}
										disabled={disabled_by_organization}
										onChange={(checked) => {
											const new_age_groups = [...accepted_age_groups]
											if (checked) {
												new_age_groups.push(age_group.value)
											} else {
												const index = new_age_groups.indexOf(age_group.value)
												if (index > -1) {
													new_age_groups.splice(index, 1)
												}
											}
											updateAcceptedAgeGroups(new_age_groups)
										}}
									/>
								</div>
							))}
						</div>
					</div>
				)}
			</Container>
		</div>
	)
}

ContactRegistrationFields.propTypes = {
	state: PropTypes.object,
	handleInputChange: PropTypes.func,
	mergeState: PropTypes.func,
	is_for_organizations: PropTypes.bool,
	confirm_digital_registrations: PropTypes.bool,
	toggleConfirmDigitalRegistrations: PropTypes.func,
	accepted_age_groups: PropTypes.array,
	updateAcceptedAgeGroups: PropTypes.func,
	organization: PropTypes.object,
}

GroupFields.propTypes = {
	name: PropTypes.string,
	title: PropTypes.string,
	value: PropTypes.number,
	handleFieldChange: PropTypes.func,
	hide_required: PropTypes.bool,
	hide: PropTypes.bool,
	disabled: PropTypes.bool,
}

export default ContactRegistrationFields
